export interface MetapropertiesOption {
	id: string;
	label: string;
	name: string;
	image?: string;
	icon?: string;
	description?: string;
	zindex?: number;
	[key: string]: unknown;
	metapropertyName?: string;
}

export interface SearchMetaproperty {
	ID: string; // Uppercase, unlike the options' `id` — that is how the endpoint serialises it
	label: string;
	name: string;
	options?: MetapropertiesOption[];
	orderBy?: 'label' | 'zindex';
	spotlightFilterAction?: string;
}

export interface SearchItem {
	id: string;
	name: string;
	description?: string;
	title?: string;
	tags?: string[];
	// We only need both assetCount ans mediaCount while migrating to the new search endpoints
	// After that, we can remove mediaCount
	mediaCount?: number;
	assetCount?: number;
	extension?: string[];
	thumbnails?: {
		large: string;
		mini: string;
	};
	thumbnailUrl?: string; // for collections endpoint v3
	path?: string; // for collections endpoint v3
	isMetapropertyOption?: boolean;
	highlight?: HighlightMatch;
}

export interface Guideline {
	id: string;
	/** `<strong>`-highlighted, raw text around the tags — see `GUIDELINE_TITLE`. */
	title: string;
	path: string;
	image: string | null;
	/** One of guidelines-search-svc's hardcoded per-type SVGs, never user data, so it may be parsed as markup. */
	iconSvg: string;
}

/** How an asset matched a hybrid search. Only ever returned by the hybrid endpoint. */
export type HybridMatchBadge = 'lexical' | 'visual';

/**
 * Which parts of one asset the search matched. Values are snippets with the match wrapped in
 * `<b>`/`</b>`; the text around the tags is **not** HTML-escaped (asset-search-svc sets no encoder).
 * Never render them as HTML: parse them with `parseHighlightedText(value, ASSET_SNIPPET)`
 * (`helpers/highlightedText.ts`) and render with `HighlightedText`.
 */
export type HighlightMatch = {
	description?: string[];
	copyright?: string[];
	tags?: string[];
	fileContent?: string[];
	transcription?: string[];
	textFromImage?: string[];
	badges?: HybridMatchBadge[];
};

/** The response-level highlighting map, keyed by asset id. */
export type Highlight = Record<string, HighlightMatch>;

type SearchSpotlightCollection = {
	assetCount: number;
	id: string;
	name: string; // `<strong>`-highlighted and HTML-escaped, or raw when unhighlighted — see `COLLECTION_NAME`
	thumbnailUrl: string; // eg "/asset/transform/123/"
	path: string; // eg "/collections/view/E83B782B-08FB-43FB-A76E214742897357"
};

export type SpotlightMediaSearchResponse = {
	count: number;
	results: SearchItem[];
	tags: TagsSearchResults;
	highlighting?: Highlight;
};

export type SpotlightCollectionsSearchResponse = {
	count: number;
	results: SearchSpotlightCollection[];
};

export type SpotlightMediaSearchInCollectionResponse = {
	results: {
		media: {
			docs: SearchItem[];
			numFound: number;
		};
	};
	highlighting?: Highlight;
	tags: TagsSearchResults;
};

export type SpotlightMetapropertiesSearchResponse = {
	count: number;
	results: SearchMetaproperty[];
};

export type SpotlightGuidelinesSearchResponse = {
	count?: number;
	results: Guideline[];
};

// Scoped by the filters applied in paramount (read from sessionStorage)
type StatelessSpotlightSearchParams = { filters: string[]; searchTerms?: string };

// Scoped to the collection being viewed
type CollectionSpotlightSearchParams = {
	searchType: 'collection';
	filterKey: string;
	collectionId: string;
	recipientShareId?: string;
};

export type SpotlightSearchParams =
	| StatelessSpotlightSearchParams
	| CollectionSpotlightSearchParams;

export type AssetsSearchResults = {
	items: SearchItem[];
	total: number;
};

export type CollectionsSearchResults = {
	items: SearchItem[];
	total: number;
};

export type GuidelinesSearchResults = {
	items: Guideline[];
	total: number;
};

// The endpoint returns: [name, count, name, count, …]
export type TagsSearchResults = (string | number)[];

export type SearchResults = {
	assets: AssetsSearchResults;
	collections: CollectionsSearchResults;
	guidelines: GuidelinesSearchResults;
	metaproperties: SearchMetaproperty[];
	tags: TagsSearchResults;
};

// handleSpotlightResults writes each response under its request key, so every value must be a
// SearchResults key — a mismatch would otherwise land on a property nothing reads.
export const TypesOfSpotlightRequests = {
	Assets: 'assets',
	Collections: 'collections',
	Metaproperties: 'metaproperties',
	Guidelines: 'guidelines',
} as const satisfies Record<string, keyof SearchResults>;

export type TypesOfSpotlightRequests =
	(typeof TypesOfSpotlightRequests)[keyof typeof TypesOfSpotlightRequests];
